import React, { useState, useEffect } from 'react';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAppId } from "../../context/AppIdContext";
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PhonebookWiseSlipSendDash = () => {
    // --- Logic State ---
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // --- Mobile Search State ---
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

    // Get context variables
    const { appId, language } = useAppId();
    const navigate = useNavigate(); // Added navigate hook

    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            title: "Phonebook Slip Status",
            subtitle: "View slip sending statistics assigned to each user.",
            searchPlaceholder: "Search by name or mobile...",
            totalAssigned: "Total",
            sent: "Sent",
            remaining: "Remaining",
            noRecordsFound: "No users found matching",
            loading: "Loading Dashboard Data...",
            admin: "Admin",
            subAdmin: "Sub Admin",
            volunteer: "Volunteer",
            boothPramukh: "Booth Pramukh",
            shaktiPramukh: "Shakti Pramukh",
            warCaptain: "War Captain",
            boothCaptain: "Booth Captain",
            liveVottingUser: "Live Voting User",
            callSurveyUser: "Call Survey User",
            voterCaptain: "Voter Captain"
        },
        hi: {
            title: "फोनबुक पर्ची स्थिति",
            subtitle: "प्रत्येक उपयोगकर्ता को सौंपे गए पर्ची भेजने के आंकड़े देखें।",
            searchPlaceholder: "नाम या मोबाइल से खोजें...",
            totalAssigned: "कुल",
            sent: "भेजा गया",
            remaining: "शेष",
            noRecordsFound: "कोई उपयोगकर्ता नहीं मिला",
            loading: "डेटा लोड हो रहा है...",
            admin: "एडमिन",
            subAdmin: "सब एडमिन",
            volunteer: "कार्यकर्ता",
            boothPramukh: "बूथ प्रमुख",
            shaktiPramukh: "शक्ति प्रमुख",
            warCaptain: "वॉर कैप्टन",
            boothCaptain: "बूथ कैप्टन",
            liveVottingUser: "लाइव वोटिंग यूज़र",
            callSurveyUser: "कॉल सर्वे यूज़र",
            voterCaptain: "वोटर कैप्टन"
        },
        gu: {
            title: "ફોનબુક સ્લિપ સ્ટેટસ",
            subtitle: "દરેક વપરાશકર્તાને સોંપેલ સ્લિપ મોકલવાના આંકડા જુઓ.",
            searchPlaceholder: "નામ અથવા મોબાઇલ દ્વારા શોધો...",
            totalAssigned: "કુલ",
            sent: "મોકલેલ",
            remaining: "બાકી",
            noRecordsFound: "કોઈ વપરાશકર્તા મળ્યો નથી",
            loading: "ડેશબોર્ડ ડેટા લોડ થઈ રહ્યો છે...",
            admin: "એડમિન",
            subAdmin: "પેટા એડમિન",
            volunteer: "કાર્યકર્તા",
            boothPramukh: "બૂથ પ્રમુખ",
            shaktiPramukh: "શક્તિ પ્રમુખ",
            warCaptain: "વોર કેપ્ટન",
            boothCaptain: "બૂથ કેપ્ટન",
            liveVottingUser: "લાઇવ વોટિંગ યુઝર",
            callSurveyUser: "કૉલ સર્વે યુઝર",
            voterCaptain: "વોટર કેપ્ટન"
        }
    };

    const txt = t[lang] || t['en'];

    const getUserTypeLabel = (type) => {
        const map = {
            'A': txt.admin,
            'SA': txt.subAdmin,
            'sa': txt.subAdmin,
            'K': txt.volunteer,
            'k': txt.volunteer,
            'BP': txt.boothPramukh,
            'BS': "Sah Booth Pramukh",
            'SP': txt.shaktiPramukh,
            'WP': txt.warCaptain,
            'BC': txt.boothCaptain,
            'LV': txt.liveVottingUser,
            'CL': txt.callSurveyUser,
            'VC': txt.voterCaptain
        };
        return map[type] || type || 'User';
    };

    // --- API Fetching ---
    useEffect(() => {
        if (appId) {
            fetchDashboardData();
        }
    }, [appId]);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_phonebook_wise_slip_send_dash`,
                { app_id: appId }
            );

            if (response.data.success && response.data.data && response.data.data.result) {
                setData(response.data.data.result);
            }
        } catch (err) {
            console.error("API Error:", err);
        } finally {
            setLoading(false);
        }
    };
    // Filter Logic
    const filteredData = data.filter(item => {
        if (!searchTerm) return true;
        const lowerSearch = searchTerm.toLowerCase();
        return (
            (item.name && item.name.toLowerCase().includes(lowerSearch)) ||
            (item.mobile_no && item.mobile_no.includes(lowerSearch))
        );
    });

    if (loading) {
        return (
            <div className="loader-wrapper">
                <div className="spinner"></div>
                <p className="loader-text">{txt.loading}</p>
                <style jsx>{`
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                    .spinner { width: 50px; height: 50px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
                    .loader-text { color: #6B7280; font-weight: 500; font-size: 1.1rem; animation: pulse 1.5s ease-in-out infinite; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
                `}</style>
            </div>
        );
    }

    return (
        <>
            <div className="app-container p-4">
                <Header title={txt.title}/>

                {/* Styled Filter Bar */}
                <div className="filter-container mt-3">
                    <div className={`search-wrapper ${isMobileSearchOpen ? 'mobile-open' : ''}`}>
                        <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder={txt.searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button className="close-search-btn" onClick={() => { setIsMobileSearchOpen(false); setSearchTerm(''); }}>
                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>

                    <div className="action-group">
                        {!isMobileSearchOpen && (
                            <button className="mobile-search-trigger" onClick={() => setIsMobileSearchOpen(true)}>
                                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                            </button>
                        )}
                    </div>
                </div>

                {/* Dashboard Grid */}
                <div className="dashboard-grid">
                    {filteredData.length > 0 ? (
                        filteredData.map((item, index) => {
                            // Calculate remaining safely
                            const remaining = (item.total || 0) - (item.send || 0);
                            
                            // Calculate percentage for progress bar safely
                            const percentage = item.total > 0 
                                ? Math.min(100, Math.round((item.send / item.total) * 100)) 
                                : 0;

                            return (
                                <div 
                                    key={index} 
                                    className="modern-stat-card"
                                    onClick={() => navigate('/phonebook_wise_slip_sending', { state: { user_id: item.admin_id } })}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="card-header user-header-layout">
                                        <div className="user-identity">
                                            <div className="modern-card-avatar">
                                                <img 
                                                    src={item.photo ? item.photo : `https://ui-avatars.com/api/?name=${encodeURIComponent(item.name)}&background=ffffff&color=0f172a`} 
                                                    onError={(e) => {
                                                        e.target.onerror = null; 
                                                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(item.name)}&background=ffffff&color=0f172a`;
                                                    }}
                                                    alt={item.name} 
                                                />
                                            </div>
                                            <div className="user-text-info">
                                                <h3 className="modern-card-name" title={item.name}>{item.name}</h3>
                                                <div className="modern-card-phone">
                                                    <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24" className="phone-icon">
                                                        <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                    </svg>
                                                    <span className="user-mobile">{item.mobile_no}</span>
                                                </div>
                                                <span className="profile-badge">{getUserTypeLabel(item.user_type)}</span>
                                            </div>
                                        </div>
                                        
                                        <div className="percentage-badge">{percentage}%</div>
                                    </div>

                                    <div className="stats-row">
                                        <div className="stat-block">
                                            <span className="stat-value text-blue">{item.total || 0}</span>
                                            <span className="stat-label">{txt.totalAssigned}</span>
                                        </div>
                                        <div className="stat-divider"></div>
                                        <div className="stat-block">
                                            <span className="stat-value text-green">{item.send || 0}</span>
                                            <span className="stat-label">{txt.sent}</span>
                                        </div>
                                        <div className="stat-divider"></div>
                                        <div className="stat-block">
                                            <span className="stat-value text-orange">{remaining > 0 ? remaining : 0}</span>
                                            <span className="stat-label">{txt.remaining}</span>
                                        </div>
                                    </div>

                                    
                                </div>
                            );
                        })
                    ) : (
                        <div className="no-results" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "40px", color: "#6b7280" }}>
                            {txt.noRecordsFound} "{searchTerm}"
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
                /* --- APP CONTAINER --- */
                .app-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }

                /* --- PROFESSIONAL FILTER CONTAINER --- */
                .filter-container {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 16px;
                    margin-bottom: 24px;
                    background: transparent;
                }

                .action-group {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .mobile-search-trigger { display: none; }
                .close-search-btn { display: none; }

                .search-wrapper {
                    position: relative;
                    flex: 1;
                    max-width: 400px;
                }

                .search-icon {
                    position: absolute;
                    left: 14px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                    width: 20px;
                    height: 20px;
                }

                .search-input {
                    width: 100%;
                    padding: 12px 16px 12px 42px;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    font-size: 14px;
                    color: #1e293b;
                    background-color: #ffffff;
                    outline: none;
                    transition: all 0.2s ease;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }

                .search-input:focus {
                    border-color: #2563eb;
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
                }

                /* --- DASHBOARD GRID & STAT CARDS --- */
                .dashboard-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
                    gap: 24px;
                    margin-top: 20px;
                }

                .modern-stat-card {
                    background: #ffffff;
                    border-radius: 16px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    border: 1px solid #f1f5f9;
                    transition: all 0.3s ease;
                    display: flex;
                    flex-direction: column;
                    padding: 24px;
                    
                    
                }

                .modern-stat-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 20px -5px rgba(0, 0, 0, 0.08);
                    border-color: #e2e8f0;
                }

                .card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 20px;
                    border-bottom: 1px solid #f1f5f9;
                    padding-bottom: 16px;
                }

                .user-identity {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                }

                .modern-card-avatar {
                    width: 56px;
                    height: 56px;
                    border-radius: 50%;
                    padding: 2px;
                    background: #e2e8f0;
                    flex-shrink: 0;
                }

                .modern-card-avatar img {
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    object-fit: cover;
                    border: none;
                }

                .user-text-info {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }

                .modern-card-name {
                    font-size: 16px;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 0 0 2px 0;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 160px;
                    line-height: 1.2;
                }

                .modern-card-phone {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    color: #64748b;
                    margin-bottom: 5px;
                }
                
                .modern-card-phone .phone-icon {
                    color: #94a3b8;
                }

                .user-mobile {
                    font-size: 13px;
                    font-weight: 500;
                    letter-spacing: 0.3px;
                }

                .profile-badge {
                    align-self: flex-start;
                    font-size: 10px;
                    font-weight: 700;
                    color: #2563eb;
                    background: #f1f5f9;
                    border: 1px solid #e2e8f0;
                    padding: 3px 10px;
                    border-radius: 20px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .percentage-badge {
                    font-size: 14px;
                    font-weight: 700;
                    color: #10b981;
                    background: #d1fae5;
                    padding: 5px 12px;
                    border-radius: 8px;
                    margin-top: 4px;
                }

                .stats-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                }

                .stat-block {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    flex: 1;
                }

                .stat-divider {
                    width: 1px;
                    height: 36px;
                    background: #e2e8f0;
                }

                .stat-value {
                    font-size: 22px;
                    font-weight: 800;
                    line-height: 1.2;
                    margin-bottom: 4px;
                }

                .text-blue { color: #2563eb; }
                .text-green { color: #10b981; }
                .text-orange { color: #f59e0b; }

                .stat-label {
                    font-size: 12px;
                    font-weight: 600;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .progress-container {
                    width: 100%;
                    height: 8px;
                    background: #f1f5f9;
                    border-radius: 4px;
                    overflow: hidden;
                    margin-top: auto;
                }

                .progress-fill {
                    height: 100%;
                    background: linear-gradient(90deg, #3b82f6 0%, #10b981 100%);
                    border-radius: 4px;
                    transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
                }

                /* Mobile Optimization */
                @media (max-width: 640px) {
                    .filter-container { flex-direction: row; align-items: center; gap: 8px; }
                    .search-wrapper { display: none; flex: 1; max-width: none; }
                    .search-wrapper.mobile-open { display: block; }
                    .search-input { padding: 10px 16px 10px 36px; font-size: 13px; padding-right: 40px; }
                    .search-icon { left: 10px; width: 16px; height: 16px; }
                    
                    .close-search-btn { 
                        display: block; 
                        position: absolute; 
                        right: 8px; 
                        top: 50%; 
                        transform: translateY(-50%); 
                        background: #f1f5f9; 
                        border: none; 
                        color: #64748b; 
                        padding: 4px; 
                        border-radius: 50%; 
                        cursor: pointer; 
                        display: flex; 
                        align-items: center; 
                        justify-content: center; 
                        z-index: 2; 
                    }
                    
                    .action-group { width: auto; flex-shrink: 0; }
                    
                    .mobile-search-trigger { 
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: white;
                        border: 1px solid #e2e8f0;
                        border-radius: 10px;
                        width: 46px;
                        height: 46px;
                        color: #64748b;
                        cursor: pointer;
                        box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                    }

                    .dashboard-grid { 
                        grid-template-columns: 1fr; 
                        gap: 16px; 
                    }

                    .modern-stat-card {
                        padding: 16px;
                    }

                    .stat-value {
                        font-size: 20px;
                    }

                    .stat-label {
                        font-size: 11px;
                    }
                    
                    .modern-card-name {
                        max-width: 140px;
                    }
                }
            `}</style>
        </>
    );
};

export default PhonebookWiseSlipSendDash;